import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ArrowRight, Plug } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const integrations = [
  {
    title: 'Raycast',
    href: '/guides/sortio-plus-raycast',
    status: 'Available',
    summary: 'Trigger Sortio sorts and switch Spaces from Raycast. Native extension setup, command palette flow, and recommended hotkeys.',
  },
  {
    title: 'Apple Shortcuts',
    href: '/guides/sortio-plus-apple-shortcuts',
    status: 'Available',
    summary: 'Drive Sortio from any Apple Shortcut on macOS or iOS. Auto-sort downloaded attachments, run rules on a schedule, or chain Sortio after a scanner shortcut.',
  },
  {
    title: 'Hazel',
    href: '/guides/sortio-plus-hazel-workflow',
    status: 'Available',
    summary: 'Use Hazel for deterministic rules and hand the messy stuff to Sortio. Recommended folder layout, example Hazel rule, and how to avoid double-moves.',
  },
  {
    title: 'Google Drive',
    href: null,
    status: 'Local sync',
    summary: 'Sortio works with the Google Drive desktop client. Point Sortio at your synced Drive folder and it sorts files locally, then the Drive client mirrors the changes. No API integration required.',
  },
  {
    title: 'Dropbox',
    href: null,
    status: 'Local sync',
    summary: 'Same model as Drive: install the Dropbox desktop client, then point Sortio at the synced folder. Sortio renames and moves files on disk and Dropbox syncs.',
  },
  {
    title: 'Clio',
    href: 'https://www.clio.com/app-directory/sortio/',
    external: true,
    status: 'Available',
    summary: 'Live in the Clio App Directory. Sortio matches local files to the right Clio matter (with OCR on scans), applies your firm’s document categories, suggests clean document names, and uploads directly from your Mac with a full audit trail.',
  },
  {
    title: 'dotloop',
    href: null,
    status: 'Coming soon',
    summary: 'Real estate transaction documents organized into per-property folders. Pulls dotloop downloads from your local sync and files them by address.',
  },
  {
    title: 'Buildium',
    href: null,
    status: 'Coming soon',
    summary: 'Property management document organization. Files leases, work orders, and tenant correspondence into the right property folder automatically.',
  },
  {
    title: 'Rent Manager',
    href: null,
    status: 'Coming soon',
    summary: 'Rent Manager document workflow integration for multifamily and commercial property managers.',
  },
  {
    title: 'Zotero',
    href: null,
    status: 'Coming soon',
    summary: 'Organize Zotero PDFs by topic, author, or citation key. Rename PDFs from content and keep your library reading-ready.',
  },
];

const IntegrationsPage = () => {
  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.getsortio.com/' },
        { '@type': 'ListItem', position: 2, name: 'Integrations', item: 'https://www.getsortio.com/integrations' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Sortio Integrations',
      url: 'https://www.getsortio.com/integrations',
    },
  ];

  return (
    <div className="min-h-screen bg-black text-apple-100 py-24">
      <SEOHead
        title="Sortio Integrations: Raycast, Apple Shortcuts, Hazel, and More"
        description="Sortio integrates with Raycast, Apple Shortcuts, Hazel, Google Drive, Dropbox, and is rolling out support for Clio, dotloop, Buildium, Rent Manager, and Zotero."
        canonical="https://www.getsortio.com/integrations"
        schema={schema}
      />

      <div className="container mx-auto px-6 max-w-4xl relative z-10">
        <Link to="/" className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-8 transition-colors group">
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>Back to Home</span>
        </Link>

        <div className="mb-12 flex flex-col items-center">
          <div className="w-14 h-14 rounded-full bg-apple-700/60 backdrop-blur-sm flex items-center justify-center mb-6 border border-apple-600/30">
            <Plug className="w-6 h-6 text-sortio-primary" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-center">
            Sortio <span className="apple-gradient-text">Integrations</span>
          </h1>
          <p className="text-apple-300 mt-4 text-center max-w-2xl">
            Sortio plays well with the tools you already use. Launchers, shortcut runners, cloud storage clients, and vertical-software like Clio or Zotero.
          </p>
        </div>

        <div className="grid gap-4">
          {integrations.map((it) => {
            const inner = (
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-xl font-medium text-apple-50 group-hover:text-sortio-primary transition-colors">{it.title}</h2>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-apple-600/40 text-apple-300 border border-apple-500/30">{it.status}</span>
                  </div>
                  <p className="text-apple-300 text-sm leading-relaxed">{it.summary}</p>
                </div>
                {it.href && (
                  <ArrowRight className="w-5 h-5 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
                )}
              </div>
            );
            if (it.href && it.external) {
              return (
                <a key={it.title} href={it.href} target="_blank" rel="noopener noreferrer" className="apple-panel p-6 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all block">
                  {inner}
                </a>
              );
            }
            return it.href ? (
              <Link key={it.title} to={it.href} className="apple-panel p-6 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all block">
                {inner}
              </Link>
            ) : (
              <div key={it.title} className="apple-panel p-6 rounded-apple group">
                {inner}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default IntegrationsPage;
